import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  FiArrowLeft,
  FiClock,
  FiUsers,
  FiVideo,
  FiExternalLink,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCcw,
  FiSave,
  FiBookOpen,
} from 'react-icons/fi'
import {
  fetchLectureDetail as fetchLectureDetailApi,
  startLecture as startLectureApi,
  endLecture as endLectureApi,
  recordAttendance as recordAttendanceApi,
} from '../../api/staffTeachingApi'
import './LectureDetail.css'

const attendanceStatuses = [
  { value: 'present', label: 'Present' },
  { value: 'late', label: 'Late' },
  { value: 'excused', label: 'Excused' },
  { value: 'absent', label: 'Absent' },
  { value: 'not_marked', label: 'Not marked' },
]

const formatDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatTime = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const titleCase = (value) => (value ? value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : '')

const LectureDetail = () => {
  const { lectureId } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isStarting, setIsStarting] = useState(false)
  const [isEnding, setIsEnding] = useState(false)
  const [attendanceDraft, setAttendanceDraft] = useState({})
  const [notesDraft, setNotesDraft] = useState({})
  const [attendanceError, setAttendanceError] = useState(null)
  const [attendanceSuccess, setAttendanceSuccess] = useState(null)
  const [isSavingAttendance, setIsSavingAttendance] = useState(false)

  const loadLectureDetail = useCallback(async () => {
    if (!lectureId) return
    setIsLoading(true)
    setError(null)
    try {
      const data = await fetchLectureDetailApi(lectureId)
      setDetail(data)
      const nextAttendance = {}
      const nextNotes = {}
      data?.attendance?.records?.forEach((record) => {
        if (record.studentId) {
          nextAttendance[record.studentId] = record.status || 'not_marked'
          if (record.notes) {
            nextNotes[record.studentId] = record.notes
          }
        }
      })
      setAttendanceDraft(nextAttendance)
      setNotesDraft(nextNotes)
    } catch (apiError) {
      console.error('Failed to load lecture detail', apiError)
      setError(apiError?.response?.data?.message || apiError.message || 'Unable to load lecture')
    } finally {
      setIsLoading(false)
    }
  }, [lectureId])

  useEffect(() => {
    loadLectureDetail()
  }, [loadLectureDetail])

  const handleStartLecture = async () => {
    if (!lectureId) return
    try {
      setIsStarting(true)
      setError(null)
      await startLectureApi(lectureId)
      await loadLectureDetail()
    } catch (apiError) {
      console.error('Failed to start lecture', apiError)
      setError(apiError?.response?.data?.message || apiError.message || 'Unable to start lecture')
    } finally {
      setIsStarting(false)
    }
  }

  const handleEndLecture = async () => {
    if (!lectureId) return
    try {
      setIsEnding(true)
      setError(null)
      await endLectureApi(lectureId)
      await loadLectureDetail()
    } catch (apiError) {
      console.error('Failed to end lecture', apiError)
      setError(apiError?.response?.data?.message || apiError.message || 'Unable to end lecture')
    } finally {
      setIsEnding(false)
    }
  }

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceDraft((prev) => ({ ...prev, [studentId]: status }))
    setAttendanceSuccess(null)
    setAttendanceError(null)
  }

  const handleNoteChange = (studentId, value) => {
    setNotesDraft((prev) => ({ ...prev, [studentId]: value }))
  }

  const timeline = detail?.timeline || {}
  const context = detail?.context || {}
  const attendanceRecords = detail?.attendance?.records || []

  const handleMarkAll = (status) => {
    const next = { ...attendanceDraft }
    attendanceRecords.forEach((record) => {
      if (record.studentId) {
        next[record.studentId] = status
      }
    })
    setAttendanceDraft(next)
    setAttendanceSuccess(null)
    setAttendanceError(null)
  }

  const handleSubmitAttendance = async (markComplete = false) => {
    if (!lectureId || !attendanceRecords.length) return
    const attendees = attendanceRecords
      .filter((record) => record.studentId && attendanceDraft[record.studentId] && attendanceDraft[record.studentId] !== 'not_marked')
      .map((record) => ({
        studentId: record.studentId,
        status: attendanceDraft[record.studentId],
        notes: notesDraft[record.studentId] || undefined,
      }))

    if (!attendees.length) {
      setAttendanceError('Select at least one attendee before saving.')
      return
    }

    try {
      setIsSavingAttendance(true)
      setAttendanceError(null)
      setAttendanceSuccess(null)
      await recordAttendanceApi(lectureId, attendees, { markComplete })
      setAttendanceSuccess(markComplete ? 'Attendance submitted and lecture completed.' : 'Attendance updated successfully.')
      await loadLectureDetail()
    } catch (apiError) {
      console.error('Failed to record attendance', apiError)
      setAttendanceError(apiError?.response?.data?.message || apiError.message || 'Unable to record attendance')
    } finally {
      setIsSavingAttendance(false)
    }
  }

  const attendanceTotals = useMemo(() => {
    const totals = detail?.attendance?.totals || {}
    const entries = Object.entries(totals)
      .filter(([key]) => key !== 'total')
      .map(([key, value]) => ({ key, value }))
    return entries
  }, [detail])

  if (!lectureId) {
    return (
      <div className="lecture-detail">
        <div className="lecture-detail__state">Lecture identifier missing. Use the lectures list to pick a session.</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="lecture-detail">
        <div className="lecture-detail__state">Loading lecture insight…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="lecture-detail">
        <div className="lecture-detail__error">
          <FiAlertCircle size={18} /> {error}
          <button type="button" onClick={loadLectureDetail}>
            <FiRefreshCcw size={16} /> Retry
          </button>
        </div>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="lecture-detail">
        <div className="lecture-detail__state">Lecture details unavailable.</div>
      </div>
    )
  }

  const lecture = detail.lecture

  return (
    <div className="lecture-detail">
      <header className="lecture-detail__header">
        <button type="button" className="back-link" onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} /> Back
        </button>
        <h1>{lecture.topic || 'Lecture detail'}</h1>
        <p>View lecture details and manage attendance</p>
      </header>

      <section className="lecture-detail__summary">
        <article>
          <span>Status</span>
          <strong className={`status-badge status-${(lecture.status || '').toLowerCase()}`}>{titleCase(lecture.status) || 'Scheduled'}</strong>
        </article>
        <article>
          <span>Scheduled</span>
          <strong>
            {formatDate(timeline.startTime)} · {formatTime(timeline.startTime)}
          </strong>
        </article>
        <article>
          <span>Ends</span>
          <strong>{formatTime(timeline.endTime)}</strong>
        </article>
        <article>
          <span>Duration</span>
          <strong>{timeline.durationMinutes ? `${timeline.durationMinutes} min` : '—'}</strong>
        </article>
      </section>

      <section className="lecture-detail__context">
        <article className="context-card">
          <header>
            <FiUsers size={18} />
            <div>
              <h2>{context.batch?.batchName || 'Batch TBD'}</h2>
              <p>{context.batch?.course?.title || 'Course detail pending'}</p>
            </div>
          </header>
          <dl>
            <div>
              <dt>Batch status</dt>
              <dd>{context.batch?.status || '—'}</dd>
            </div>
            <div>
              <dt>Timeline</dt>
              <dd>
                {formatDate(context.batch?.startDate)} – {formatDate(context.batch?.endDate)}
              </dd>
            </div>
          </dl>
          {context.batch?._id ? (
            <Link to={`/batches/${context.batch._id}`} className="text-link">
              View batch
            </Link>
          ) : (
            <span className="text-link disabled">Batch unavailable</span>
          )}
        </article>

        <article className="context-card">
          <header>
            <FiBookOpen size={18} />
            <div>
              <h2>{context.module?.title || 'Batch-level lecture'}</h2>
              <p>{context.module?.description || 'This session is not tied to a specific module.'}</p>
            </div>
          </header>
          <dl>
            <div>
              <dt>Module order</dt>
              <dd>{context.module?.order ?? '—'}</dd>
            </div>
            <div>
              <dt>Duration</dt>
              <dd>{context.module?.duration || '—'}</dd>
            </div>
          </dl>
        </article>

        <article className="context-card">
          <header>
            <FiVideo size={18} />
            <div>
              <h2>Session logistics</h2>
              <p>Meeting links and resources for this lecture</p>
            </div>
          </header>
          <dl>
            <div>
              <dt>Google Meet</dt>
              <dd>
                {lecture.meetingLink ? (
                  <a href={lecture.meetingLink} target="_blank" rel="noopener noreferrer">
                    Join Google Meet <FiExternalLink size={14} />
                  </a>
                ) : (
                  'No meeting link'
                )}
              </dd>
            </div>
            <div>
              <dt>Recording</dt>
              <dd>
                {lecture.recordingLink ? (
                  <a href={lecture.recordingLink} target="_blank" rel="noopener noreferrer">
                    View recording <FiExternalLink size={14} />
                  </a>
                ) : (
                  'Not available'
                )}
              </dd>
            </div>
          </dl>
          <div className="session-actions">
            <button type="button" onClick={handleStartLecture} disabled={isStarting || lecture.status === 'live'}>
              <FiCheckCircle size={16} /> {isStarting ? 'Starting…' : lecture.status === 'live' ? 'Live' : 'Start session'}
            </button>
            <button type="button" onClick={handleEndLecture} disabled={isEnding || lecture.status === 'completed'}>
              <FiClock size={16} /> {isEnding ? 'Completing…' : lecture.status === 'completed' ? 'Completed' : 'End session'}
            </button>
          </div>
        </article>

      </section>

      <section className="attendance-panel">
        <header className="attendance-panel__header">
          <div>
            <h2>Attendance</h2>
            <p>
              Last updated {detail.attendance?.lastUpdated ? `${formatDate(detail.attendance.lastUpdated)} · ${formatTime(detail.attendance.lastUpdated)}` : '—'}
            </p>
          </div>
          <div className="attendance-summary">
            {attendanceTotals.map((item) => (
              <span key={item.key}>
                {item.value} {titleCase(item.key)}
              </span>
            ))}
          </div>
        </header>

        {attendanceError && (
          <div className="attendance-alert is-error">
            <FiAlertCircle size={16} /> {attendanceError}
          </div>
        )}

        {attendanceSuccess && (
          <div className="attendance-alert is-success">
            <FiCheckCircle size={16} /> {attendanceSuccess}
          </div>
        )}

        <div className="attendance-toolbar">
          <button type="button" onClick={() => handleMarkAll('present')}>
            Mark all present
          </button>
          <button type="button" onClick={() => handleMarkAll('not_marked')}>
            Clear selections
          </button>
        </div>

        {attendanceRecords.length === 0 ? (
          <div className="attendance-empty">No students enrolled in this batch yet.</div>
        ) : (
          <div className="attendance-table" role="table">
            <div className="attendance-row attendance-row--header" role="row">
              <div role="columnheader">Student</div>
              <div role="columnheader">Email</div>
              <div role="columnheader">Status</div>
              <div role="columnheader">Notes</div>
            </div>
            {attendanceRecords.map((record, index) => {
              const studentId = record.studentId
              const attendanceValue = studentId ? attendanceDraft[studentId] || 'not_marked' : record.status
              return (
                <div
                  key={record.enrollmentId || record.studentId || `orphan-${index}`}
                  className="attendance-row"
                  role="row"
                >
                  <div role="cell">
                    <strong>
                      {record.user?.firstName} {record.user?.lastName}
                    </strong>
                    {record.isExternal && <span className="badge">Guest</span>}
                  </div>
                  <div role="cell">{record.user?.email || '—'}</div>
                  <div role="cell" className="attendance-select">
                    {studentId ? (
                      <select
                        value={attendanceValue}
                        onChange={(event) => handleAttendanceChange(studentId, event.target.value)}
                      >
                        {attendanceStatuses.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{record.status || '—'}</span>
                    )}
                  </div>
                  <div role="cell" className="attendance-notes">
                    {studentId ? (
                      <input
                        type="text"
                        value={notesDraft[studentId] || ''}
                        placeholder="Optional notes"
                        onChange={(event) => handleNoteChange(studentId, event.target.value)}
                      />
                    ) : (
                      <span>{record.notes || '—'}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <footer className="attendance-actions">
          <button
            type="button"
            className="muted-button"
            onClick={() => handleSubmitAttendance(false)}
            disabled={isSavingAttendance}
          >
            <FiSave size={16} /> {isSavingAttendance ? 'Saving…' : 'Save attendance'}
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => handleSubmitAttendance(true)}
            disabled={isSavingAttendance || lecture.status === 'completed'}
          >
            <FiCheckCircle size={16} />{' '}
            {lecture.status === 'completed' ? 'Already completed' : isSavingAttendance ? 'Submitting…' : 'Submit & complete'}
          </button>
        </footer>
      </section>
    </div>
  )
}

export default LectureDetail
